import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const PreRekiChecklist = () => {
  const baseURL = import.meta.env.VITE_API_BASE_URL || '';
  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState('all');
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraItemId, setCameraItemId] = useState(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [expandedImage, setExpandedImage] = useState(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [addingTask, setAddingTask] = useState(false);

  // Predefined Tasks Modal State
  const [showPredefinedTasksModal, setShowPredefinedTasksModal] = useState(false);
  const [predefinedTasks, setPredefinedTasks] = useState([]);
  const [loadingPredefinedTasks, setLoadingPredefinedTasks] = useState(false);
  const [selectedPredefinedTasks, setSelectedPredefinedTasks] = useState([]);
  const [predefinedTaskSearch, setPredefinedTaskSearch] = useState('');
  const [addingPredefinedTasks, setAddingPredefinedTasks] = useState(false);
  const [showManagePredefinedTasks, setShowManagePredefinedTasks] = useState(false);
  const [newPredefinedTaskName, setNewPredefinedTaskName] = useState('');
  const [newPredefinedTaskDescription, setNewPredefinedTaskDescription] = useState('');
  const [newPredefinedTaskCategory, setNewPredefinedTaskCategory] = useState('');
  const [creatingPredefinedTask, setCreatingPredefinedTask] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null); // For category-based navigation
  const [syncingTasks, setSyncingTasks] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [supportsCameraSwitch, setSupportsCameraSwitch] = useState(false);

  // Fetch gyms list
  const fetchGyms = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${baseURL}/api/reki/gyms?type=pre_reki`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setGyms(data.gyms || []);
      }
    } catch (error) {
      console.error('Error fetching gyms:', error);
      toast.error('Failed to load gyms');
    } finally {
      setLoading(false);
    }
  };

  // Fetch checklist for selected gym
  const fetchChecklist = async (gymId) => {
    if (!gymId || gymId === 'all') {
      setChecklist(null);
      return;
    }
    try {
      setLoadingChecklist(true);
      const response = await fetch(`${baseURL}/api/reki/gym/${gymId}/pre_reki`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(data.checklist);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast.error('Failed to load checklist');
    } finally {
      setLoadingChecklist(false);
    }
  };

  useEffect(() => {
    fetchGyms();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (selectedGymId && selectedGymId !== 'all') {
      fetchChecklist(selectedGymId);
    } else {
      setChecklist(null);
    }
  }, [selectedGymId]);

  const selectedGym = gyms.find(g => g.id === selectedGymId);

  // Handle marking item complete/incomplete
  const handleCheckItem = async (itemId, isCompleted) => {
    try {
      const response = await fetch(`${baseURL}/api/reki/item/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isCompleted })
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(prev => ({
          ...prev,
          status: data.checklistStatus,
          items: prev.items.map(item =>
            item.id === itemId ? { ...item, ...data.item } : item
          )
        }));
        toast.success(isCompleted ? 'Task completed' : 'Task unchecked');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update task');
    }
  };

  // Handle file upload
  const handleFileUpload = async (itemId, file, isFromCamera = false) => {
    if (!file) return;
    setUploadingItem(itemId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('isFromCamera', isFromCamera);
    formData.append('mediaType', 'before');

    // Get GPS location
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000
          });
        });
        formData.append('gpsLatitude', position.coords.latitude);
        formData.append('gpsLongitude', position.coords.longitude);
      } catch (e) {
        console.log('Could not get GPS location');
      }
    }

    try {
      const response = await fetch(`${baseURL}/api/reki/item/${itemId}/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId
              ? { ...item, media: [...(item.media || []), data.media] }
              : item
          )
        }));
        toast.success('Photo uploaded');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingItem(null);
    }
  };

  // Handle delete media
  const handleDeleteMedia = async (mediaId, itemId) => {
    if (!confirm('Delete this photo?')) return;
    try {
      const response = await fetch(`${baseURL}/api/reki/media/${mediaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setChecklist(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId
              ? { ...item, media: item.media.filter(m => m.id !== mediaId) }
              : item
          )
        }));
        toast.success('Photo deleted');
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete');
    }
  };

  // Handle save remark
  const handleSaveRemark = async () => {
    if (!selectedItem) return;
    try {
      const response = await fetch(`${baseURL}/api/reki/item/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hasIssue: remarkText.trim().length > 0,
          issueDescription: remarkText.trim()
        })
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.id === selectedItem.id ? { ...item, ...data.item } : item
          )
        }));
        toast.success('Remark saved');
        setShowRemarkModal(false);
        setSelectedItem(null);
        setRemarkText('');
      }
    } catch (error) {
      console.error('Error saving remark:', error);
      toast.error('Failed to save remark');
    }
  };

  // Add custom task
  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    if (!checklist?.id) return;

    setAddingTask(true);
    try {
      const response = await fetch(`${baseURL}/api/reki/item/${checklist.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTaskTitle.trim(),
          description: newTaskDescription.trim(),
          category: 'Custom',
          isMandatory: false,
          isCustom: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(prev => ({
          ...prev,
          items: [...prev.items, data.item]
        }));
        toast.success('Custom task added');
        setShowAddTaskModal(false);
        setNewTaskTitle('');
        setNewTaskDescription('');
      } else {
        toast.error('Failed to add task');
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    } finally {
      setAddingTask(false);
    }
  };

  // Delete task from checklist
  const handleDeleteTask = async (itemId) => {
    if (!confirm('Delete this task from the checklist?')) return;
    try {
      const response = await fetch(`${baseURL}/api/reki/item/${itemId}?force=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setChecklist(prev => ({
          ...prev,
          items: prev.items.filter(item => item.id !== itemId)
        }));
        toast.success('Task deleted');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Clear all tasks from checklist
  const handleClearAllTasks = async () => {
    if (!checklist?.items?.length) return;
    if (!confirm(`Are you sure you want to delete all ${checklist.items.length} tasks? This cannot be undone.`)) return;

    try {
      // Delete all items one by one
      for (const item of checklist.items) {
        await fetch(`${baseURL}/api/reki/item/${item.id}?force=true`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }
      setChecklist(prev => ({
        ...prev,
        items: []
      }));
      toast.success('All tasks cleared');
    } catch (error) {
      console.error('Error clearing tasks:', error);
      toast.error('Failed to clear all tasks');
      // Refresh to get current state
      fetchChecklist(selectedGymId);
    }
  };

  // Fetch predefined tasks (auto-seed if empty)
  const fetchPredefinedTasks = async () => {
    setLoadingPredefinedTasks(true);
    try {
      const response = await fetch(`${baseURL}/api/predefined-tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();

        // If no tasks exist, seed default tasks
        if (!data.tasks || data.tasks.length === 0) {
          await seedPredefinedTasks();
          return; // seedPredefinedTasks will call fetchPredefinedTasks again
        }

        setPredefinedTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching predefined tasks:', error);
      toast.error('Failed to load predefined tasks');
    } finally {
      setLoadingPredefinedTasks(false);
    }
  };

  // Seed default predefined tasks
  const seedPredefinedTasks = async () => {
    try {
      const response = await fetch(`${baseURL}/api/predefined-tasks/seed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force: true })
      });

      if (response.ok) {
        const data = await response.json();
        setPredefinedTasks(data.tasks || []);
        toast.success(`${data.created} predefined tasks loaded`);
      } else {
        // If seed fails (maybe tasks already exist), just fetch again
        const fetchResponse = await fetch(`${baseURL}/api/predefined-tasks`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (fetchResponse.ok) {
          const fetchData = await fetchResponse.json();
          setPredefinedTasks(fetchData.tasks || []);
        }
      }
    } catch (error) {
      console.error('Error seeding predefined tasks:', error);
    } finally {
      setLoadingPredefinedTasks(false);
    }
  };

  // Open predefined tasks modal
  const openPredefinedTasksModal = () => {
    setShowPredefinedTasksModal(true);
    setSelectedPredefinedTasks([]);
    setPredefinedTaskSearch('');
    setSelectedCategory(null);
    fetchPredefinedTasks();
  };

  // Toggle task selection
  const toggleTaskSelection = (taskId) => {
    setSelectedPredefinedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Select/Deselect all tasks
  const toggleSelectAll = () => {
    const filteredTasks = predefinedTasks.filter(task =>
      task.name.toLowerCase().includes(predefinedTaskSearch.toLowerCase()) ||
      (task.category && task.category.toLowerCase().includes(predefinedTaskSearch.toLowerCase()))
    );
    if (selectedPredefinedTasks.length === filteredTasks.length) {
      setSelectedPredefinedTasks([]);
    } else {
      setSelectedPredefinedTasks(filteredTasks.map(t => t.id));
    }
  };

  // Add selected predefined tasks to checklist
  const handleAddPredefinedTasks = async () => {
    if (selectedPredefinedTasks.length === 0) {
      toast.error('Please select at least one task');
      return;
    }
    if (!checklist?.id) return;

    setAddingPredefinedTasks(true);
    try {
      const selectedTasksData = predefinedTasks.filter(t => selectedPredefinedTasks.includes(t.id));
      const addedItems = [];

      for (const task of selectedTasksData) {
        const response = await fetch(`${baseURL}/api/reki/item/${checklist.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: task.name,
            description: task.description || '',
            category: task.category || 'Custom',
            isMandatory: false,
            isCustom: true
          })
        });
        if (response.ok) {
          const data = await response.json();
          addedItems.push(data.item);
        }
      }

      if (addedItems.length > 0) {
        setChecklist(prev => ({
          ...prev,
          items: [...prev.items, ...addedItems]
        }));
        toast.success(`${addedItems.length} task${addedItems.length > 1 ? 's' : ''} added`);
        setShowPredefinedTasksModal(false);
        setSelectedPredefinedTasks([]);
      }
    } catch (error) {
      console.error('Error adding predefined tasks:', error);
      toast.error('Failed to add tasks');
    } finally {
      setAddingPredefinedTasks(false);
    }
  };

  // Sync facility tasks from Kanban to Pre-Reki checklist
  const handleSyncFacilityTasks = async () => {
    if (!selectedGymId || selectedGymId === 'all') {
      toast.error('Please select a gym first');
      return;
    }

    setSyncingTasks(true);
    try {
      const response = await fetch(`${baseURL}/api/facility-task/sync-to-pre-reki`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gym_id: selectedGymId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.synced > 0) {
          toast.success(`Synced ${data.synced} tasks from Kanban`);
          // Refresh the checklist to show new items
          await fetchChecklist(selectedGymId);
        } else {
          toast.info(data.message || 'No new tasks to sync');
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to sync tasks');
      }
    } catch (error) {
      console.error('Error syncing facility tasks:', error);
      toast.error('Failed to sync tasks');
    } finally {
      setSyncingTasks(false);
    }
  };

  // Create new predefined task
  const handleCreatePredefinedTask = async () => {
    if (!newPredefinedTaskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }

    setCreatingPredefinedTask(true);
    try {
      const response = await fetch(`${baseURL}/api/predefined-tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newPredefinedTaskName.trim(),
          description: newPredefinedTaskDescription.trim(),
          category: newPredefinedTaskCategory.trim() || 'Custom'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPredefinedTasks(prev => [...prev, data.task]);
        toast.success('Predefined task created');
        setNewPredefinedTaskName('');
        setNewPredefinedTaskDescription('');
        setNewPredefinedTaskCategory('');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating predefined task:', error);
      toast.error('Failed to create task');
    } finally {
      setCreatingPredefinedTask(false);
    }
  };

  // Delete predefined task
  const handleDeletePredefinedTask = async (taskId) => {
    if (!confirm('Delete this predefined task permanently?')) return;

    try {
      const response = await fetch(`${baseURL}/api/predefined-tasks/${taskId}?permanent=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setPredefinedTasks(prev => prev.filter(t => t.id !== taskId));
        setSelectedPredefinedTasks(prev => prev.filter(id => id !== taskId));
        toast.success('Predefined task deleted');
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting predefined task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Filter predefined tasks by search
  const filteredPredefinedTasks = predefinedTasks.filter(task =>
    task.name.toLowerCase().includes(predefinedTaskSearch.toLowerCase()) ||
    (task.category && task.category.toLowerCase().includes(predefinedTaskSearch.toLowerCase())) ||
    (task.description && task.description.toLowerCase().includes(predefinedTaskSearch.toLowerCase()))
  );

  // Camera functions
  const openCamera = async (itemId) => {
    setCameraItemId(itemId);
    setShowCameraModal(true);
    setCapturedImage(null);
    setFacingMode('environment'); // Reset to back camera
    try {
      // Check if device has multiple cameras
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setSupportsCameraSwitch(videoDevices.length > 1);
      } catch (error) {
        console.warn('Could not enumerate devices:', error);
        setSupportsCameraSwitch(false);
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Could not access camera');
      setShowCameraModal(false);
    }
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);

    // Stop current stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    // Start with new facing mode
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error switching camera:', error);
      toast.error('Unable to switch camera');
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(dataUrl);
    }
  };

  const savePhoto = async () => {
    if (!capturedImage || !cameraItemId) return;
    const response = await fetch(capturedImage);
    const blob = await response.blob();
    const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
    await handleFileUpload(cameraItemId, file, true);
    closeCamera();
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
    setCapturedImage(null);
    setShowCameraModal(false);
    setCameraItemId(null);
  };

  // Calculate progress
  const progress = checklist?.items?.length > 0
    ? Math.round((checklist.items.filter(i => i.isCompleted).length / checklist.items.length) * 100)
    : 0;
  const completedCount = checklist?.items?.filter(i => i.isCompleted).length || 0;
  const totalCount = checklist?.items?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-white/60 rounded-xl w-full"></div>
            <div className="h-32 bg-white/60 rounded-2xl"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 bg-white/60 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      {/* Sticky Header with Gym Filter */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-orange-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Pre-Reki</h1>
                <p className="text-xs text-gray-500">Document issues before acquisition</p>
              </div>
            </div>
            <div className="flex-1 ml-4">
              <select
                value={selectedGymId}
                onChange={(e) => setSelectedGymId(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
              >
                <option value="all">Select a Gym...</option>
                {gyms.map(gym => (
                  <option key={gym.id} value={gym.id}>{gym.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* No Gym Selected State */}
        {(!selectedGymId || selectedGymId === 'all') && (
          <div className="bg-white rounded-2xl shadow-lg shadow-orange-100/50 p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Select a Gym</h3>
            <p className="text-gray-500 mb-6">Choose a gym from the dropdown above to view and complete the Pre-Reki checklist</p>
            <div className="inline-flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-4 py-2 rounded-full">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {gyms.length} gyms available
            </div>
          </div>
        )}

        {/* Loading Checklist */}
        {loadingChecklist && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading checklist...</p>
          </div>
        )}

        {/* Checklist Display */}
        {selectedGym && checklist && !loadingChecklist && (
          <>
            {/* Progress Card */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 mb-6 shadow-xl shadow-orange-200/50">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedGym.name}</h2>
                  <p className="text-orange-100 text-sm">{selectedGym.location || selectedGym.address}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white">{progress}%</div>
                  <div className="text-orange-100 text-sm">{completedCount}/{totalCount} done</div>
                </div>
              </div>
              <div className="w-full bg-white/30 rounded-full h-2">
                <div
                  className="bg-white h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Photo Reminder Note */}
              <div className="mt-3 flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2">
                <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-white text-sm font-medium">Please add a photo with each task</p>
              </div>

              {/* Add Task Buttons */}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={openPredefinedTasksModal}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-orange-600 rounded-xl font-medium hover:bg-orange-50 transition-colors shadow-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Select Predefined Tasks
                </button>
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl font-medium transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Custom Task
                </button>
              </div>
              {/* Manage & Clear Tasks Links */}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleSyncFacilityTasks}
                  disabled={syncingTasks}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {syncingTasks ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  {syncingTasks ? 'Syncing...' : 'Sync from Kanban'}
                </button>
                <button
                  onClick={() => {
                    setShowPredefinedTasksModal(true);
                    setShowManagePredefinedTasks(true);
                    fetchPredefinedTasks();
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-white/80 hover:text-white text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Manage Predefined Tasks
                </button>
                {checklist.items?.length > 0 && (
                  <button
                    onClick={handleClearAllTasks}
                    className="flex items-center gap-2 px-4 py-2 text-red-200 hover:text-red-100 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Clear All Tasks
                  </button>
                )}
              </div>
            </div>

            {/* Numbered Checklist */}
            <div className="space-y-3">
              {/* Task Cards */}
              {checklist.items?.map((item, index) => {
                const beforePhotos = item.media?.filter(m => m.mediaType === 'before') || [];
                const isUploading = uploadingItem === item.id;

                return (
                  <div
                    key={item.id}
                    className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden border-l-4 ${
                      item.isCompleted ? 'border-green-500' : item.hasIssue ? 'border-orange-500' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-4">
                      {/* Task Header Row */}
                      <div className="flex items-start gap-3">
                        {/* Number Badge */}
                        <div
                          onClick={() => handleCheckItem(item.id, !item.isCompleted)}
                          className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg cursor-pointer transition-all ${
                            item.isCompleted
                              ? 'bg-green-500 text-white shadow-lg shadow-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-orange-100 hover:text-orange-600'
                          }`}
                        >
                          {item.isCompleted ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </div>

                        {/* Task Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <h3 className={`font-semibold text-gray-900 ${item.isCompleted ? 'line-through text-gray-400' : ''}`}>
                                {item.title}
                              </h3>
                              {item.description && (
                                <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
                              )}
                              {/* Tags */}
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {item.isMandatory && (
                                  <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-medium rounded-full">Required</span>
                                )}
                                {item.isCustom && (
                                  <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs font-medium rounded-full">Custom/Predefined</span>
                                )}
                                {item.hasIssue && (
                                  <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs font-medium rounded-full">Has Issue</span>
                                )}
                                {beforePhotos.length > 0 && (
                                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                                    {beforePhotos.length} Photo{beforePhotos.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openCamera(item.id)}
                                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                title="Take Photo"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </button>
                              <label className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleFileUpload(item.id, e.target.files[0], false);
                                    }
                                  }}
                                />
                              </label>
                              <button
                                onClick={() => {
                                  setSelectedItem(item);
                                  setRemarkText(item.issueDescription || '');
                                  setShowRemarkModal(true);
                                }}
                                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                title="Add Remark"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteTask(item.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Task"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Before Photos Thumbnails */}
                      {(beforePhotos.length > 0 || isUploading) && (
                        <div className="mt-3 pl-13 ml-13">
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {beforePhotos.map(media => (
                              <div key={media.id} className="relative flex-shrink-0 group">
                                <img
                                  src={`${baseURL}${media.url}`}
                                  alt="Before"
                                  className="w-16 h-16 object-cover rounded-lg border-2 border-orange-200 cursor-pointer hover:border-orange-400 transition-colors"
                                  onClick={() => setExpandedImage({ url: `${baseURL}${media.url}`, type: 'Before' })}
                                />
                                <button
                                  onClick={() => handleDeleteMedia(media.id, item.id)}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                <span className="absolute bottom-0.5 left-0.5 text-[8px] bg-orange-600 text-white px-1 py-0.5 rounded">Before</span>
                              </div>
                            ))}
                            {isUploading && (
                              <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <div className="animate-spin w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Remark Display */}
                      {item.issueDescription && (
                        <div className="mt-3 ml-13 pl-13">
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className="text-sm text-orange-800">{item.issueDescription}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-500 to-amber-500">
              <h3 className="font-bold text-white">Capture Before Photo</h3>
              <button onClick={closeCamera} className="text-white/80 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {!capturedImage ? (
                <div className="relative">
                  <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl" />
                  {/* Camera Switch Button */}
                  {supportsCameraSwitch && (
                    <button
                      onClick={switchCamera}
                      className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 transition-colors shadow-lg"
                      title={`Switch to ${facingMode === 'user' ? 'back' : 'front'} camera`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  )}
                  {/* Camera Mode Indicator */}
                  <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2.5 py-1.5 rounded-full flex items-center gap-1.5">
                    {facingMode === 'user' ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Front
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Back
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <img src={capturedImage} alt="Captured" className="w-full rounded-xl" />
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="p-4 border-t flex justify-center gap-4">
              {!capturedImage ? (
                <button
                  onClick={capturePhoto}
                  className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg transition-shadow"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  </svg>
                  Capture
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setCapturedImage(null)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold"
                  >
                    Retake
                  </button>
                  <button
                    onClick={savePhoto}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Use Photo
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remark Modal */}
      {showRemarkModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-amber-500">
              <h3 className="font-bold text-white">Add Problem / Remark</h3>
              <p className="text-orange-100 text-sm">{selectedItem.title}</p>
            </div>
            <div className="p-4">
              <textarea
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                rows={4}
                placeholder="Describe the issue or problem found..."
              />
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRemarkModal(false);
                  setSelectedItem(null);
                  setRemarkText('');
                }}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRemark}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold"
              >
                Save Remark
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Task Modal */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-amber-500">
              <h3 className="font-bold text-white">Add Custom Task</h3>
              <p className="text-orange-100 text-sm">Create a new checklist item</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Title *</label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter task title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows={3}
                  placeholder="Add description..."
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  setNewTaskTitle('');
                  setNewTaskDescription('');
                }}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                disabled={addingTask}
              >
                Cancel
              </button>
              <button
                onClick={handleAddTask}
                disabled={addingTask || !newTaskTitle.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {addingTask ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={expandedImage.url}
              alt={expandedImage.type}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <div className="absolute top-4 left-4 px-3 py-1 bg-orange-600 text-white text-sm font-medium rounded-full">
              {expandedImage.type}
            </div>
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Predefined Tasks Selection Modal - Fully Responsive */}
      {showPredefinedTasksModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col rounded-t-2xl">
            {/* Header - Responsive */}
            <div className="p-3 sm:p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                {/* Back button when in manage tasks view on mobile */}
                {showManagePredefinedTasks && (
                  <button
                    onClick={() => setShowManagePredefinedTasks(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors sm:hidden"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg truncate">
                    {showManagePredefinedTasks
                      ? 'Manage Tasks'
                      : 'Select Predefined Tasks'}
                  </h3>
                  <p className="text-gray-500 text-xs sm:text-sm truncate hidden sm:block">
                    {showManagePredefinedTasks
                      ? 'Create and manage your predefined tasks'
                      : 'Choose a category and select tasks'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowManagePredefinedTasks(!showManagePredefinedTasks);
                    if (!showManagePredefinedTasks) setSelectedCategory(null);
                  }}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    showManagePredefinedTasks
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="hidden sm:inline">{showManagePredefinedTasks ? 'Back to Tasks' : 'Manage Tasks'}</span>
                  <span className="sm:hidden">{showManagePredefinedTasks ? 'Tasks' : 'Manage'}</span>
                </button>
                <button
                  onClick={() => {
                    setShowPredefinedTasksModal(false);
                    setShowManagePredefinedTasks(false);
                    setSelectedCategory(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Manage Tasks View - Responsive */}
            {showManagePredefinedTasks ? (
              <div className="flex-1 overflow-y-auto">
                {/* Refresh Tasks Button - Responsive */}
                <div className="p-3 sm:p-4 border-b bg-orange-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Refresh Default Tasks</h4>
                      <p className="text-xs sm:text-sm text-gray-500">Load latest predefined tasks from the system</p>
                    </div>
                    <button
                      onClick={async () => {
                        if (confirm('This will reset all predefined tasks to the latest defaults. Any custom tasks you created will be removed. Continue?')) {
                          setLoadingPredefinedTasks(true);
                          await seedPredefinedTasks();
                        }
                      }}
                      disabled={loadingPredefinedTasks}
                      className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {loadingPredefinedTasks ? 'Refreshing...' : 'Refresh Tasks'}
                    </button>
                  </div>
                </div>

                {/* Create New Task Form - Responsive */}
                <div className="p-3 sm:p-4 border-b bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Create New Predefined Task</h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={newPredefinedTaskName}
                      onChange={(e) => setNewPredefinedTaskName(e.target.value)}
                      placeholder="Task name *"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <input
                      type="text"
                      value={newPredefinedTaskDescription}
                      onChange={(e) => setNewPredefinedTaskDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newPredefinedTaskCategory}
                        onChange={(e) => setNewPredefinedTaskCategory(e.target.value)}
                        placeholder="Category (e.g., Branding, Lighting)"
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      <button
                        onClick={handleCreatePredefinedTask}
                        disabled={creatingPredefinedTask || !newPredefinedTaskName.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                      >
                        {creatingPredefinedTask ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Existing Tasks List with Delete */}
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Existing Predefined Tasks ({predefinedTasks.length})</h4>
                  {loadingPredefinedTasks ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : predefinedTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No predefined tasks yet. Create one above!
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {predefinedTasks.map(task => (
                        <div
                          key={task.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-orange-200 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{task.name}</div>
                            <div className="flex items-center gap-2 mt-1">
                              {task.category && (
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                                  {task.category}
                                </span>
                              )}
                              {task.description && (
                                <span className="text-xs text-gray-500 truncate">{task.description}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeletePredefinedTask(task.id)}
                            className="ml-2 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Task"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
) : (
              /* Category Dropdown + Tasks View - Fully Responsive */
              <>
                {/* Category Dropdown Selector - Responsive */}
                <div className="p-3 sm:p-4 border-b bg-gray-50">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Select Category *
                  </label>
                  <div className="relative">
                    <select
                      value={selectedCategory || ''}
                      onChange={(e) => setSelectedCategory(e.target.value || null)}
                      className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 pr-10 text-sm sm:text-base text-gray-900 font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer"
                    >
                      <option value="">-- Select a Category --</option>
                      {Object.entries(
                        predefinedTasks.reduce((acc, task) => {
                          const cat = task.category || 'Other';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(task);
                          return acc;
                        }, {})
                      )
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([category, tasks]) => {
                          const selectedInCategory = tasks.filter(t => selectedPredefinedTasks.includes(t.id)).length;
                          return (
                            <option key={category} value={category}>
                              {category} ({tasks.length}){selectedInCategory > 0 ? ` - ${selectedInCategory} selected` : ''}
                            </option>
                          );
                        })}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {selectedPredefinedTasks.length > 0 && (
                    <p className="mt-2 text-xs sm:text-sm text-orange-600 font-medium">
                      {selectedPredefinedTasks.length} task{selectedPredefinedTasks.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Tasks Grid - Responsive Inline Checkboxes */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                  {loadingPredefinedTasks ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : !selectedCategory ? (
                    <div className="text-center py-8 sm:py-12">
                      <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <p className="text-gray-500 text-sm sm:text-lg">Select a category from dropdown</p>
                      <p className="text-gray-400 text-xs sm:text-sm mt-1">Tasks will appear here</p>
                    </div>
                  ) : (
                    <>
                      {/* Select All for Category - Responsive */}
                      <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate mr-2">
                          {selectedCategory}
                        </h4>
                        <button
                          onClick={() => {
                            const categoryTasks = predefinedTasks.filter(t => (t.category || 'Other') === selectedCategory);
                            const categoryTaskIds = categoryTasks.map(t => t.id);
                            const allSelected = categoryTaskIds.every(id => selectedPredefinedTasks.includes(id));
                            if (allSelected) {
                              setSelectedPredefinedTasks(prev => prev.filter(id => !categoryTaskIds.includes(id)));
                            } else {
                              setSelectedPredefinedTasks(prev => [...new Set([...prev, ...categoryTaskIds])]);
                            }
                          }}
                          className="text-xs sm:text-sm text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
                        >
                          {(() => {
                            const categoryTasks = predefinedTasks.filter(t => (t.category || 'Other') === selectedCategory);
                            const categoryTaskIds = categoryTasks.map(t => t.id);
                            const allSelected = categoryTaskIds.every(id => selectedPredefinedTasks.includes(id));
                            return allSelected ? 'Deselect All' : 'Select All';
                          })()}
                        </button>
                      </div>

                      {/* Tasks as Responsive Inline Checkboxes Grid */}
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        {predefinedTasks
                          .filter(t => (t.category || 'Other') === selectedCategory)
                          .map(task => {
                            const isSelected = selectedPredefinedTasks.includes(task.id);
                            return (
                              <label
                                key={task.id}
                                className={`inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? 'bg-orange-50 border-orange-300 text-orange-700'
                                    : 'bg-white border-gray-200 text-gray-700 hover:border-orange-200 hover:bg-orange-50/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleTaskSelection(task.id)}
                                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
                                />
                                <span className="text-xs sm:text-sm font-medium">
                                  {task.name}
                                </span>
                              </label>
                            );
                          })}
                      </div>
                    </>
                  )}
                </div>

                {/* Footer - Responsive */}
                <div className="p-3 sm:p-4 border-t bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sticky bottom-0">
                  {/* Mobile: Selected count + Add button row */}
                  <div className="flex items-center justify-between sm:hidden">
                    <span className="text-xs text-gray-500">
                      {selectedPredefinedTasks.length > 0
                        ? `${selectedPredefinedTasks.length} selected`
                        : 'No tasks selected'}
                    </span>
                    <button
                      onClick={() => setShowManagePredefinedTasks(true)}
                      className="text-orange-600 font-medium text-xs hover:underline"
                    >
                      + New Task
                    </button>
                  </div>

                  {/* Desktop: Create New Task link */}
                  <button
                    onClick={() => setShowManagePredefinedTasks(true)}
                    className="hidden sm:flex text-orange-600 font-medium text-sm hover:underline items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Task
                  </button>

                  {/* Action buttons */}
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setShowPredefinedTasksModal(false);
                        setShowManagePredefinedTasks(false);
                        setSelectedCategory(null);
                      }}
                      className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddPredefinedTasks}
                      disabled={addingPredefinedTasks || selectedPredefinedTasks.length === 0}
                      className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                    >
                      {addingPredefinedTasks ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          <span className="hidden sm:inline">Adding...</span>
                        </>
                      ) : (
                        <>
                          <span className="sm:hidden">Add {selectedPredefinedTasks.length > 0 ? `(${selectedPredefinedTasks.length})` : ''}</span>
                          <span className="hidden sm:inline">Add {selectedPredefinedTasks.length > 0 ? `${selectedPredefinedTasks.length} Task${selectedPredefinedTasks.length > 1 ? 's' : ''}` : 'Tasks'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreRekiChecklist;
